/**
 * PathCourseList Component
 *
 * Displays the list of courses in a Learning Path with user progress on each course.
 * Used in CourseDetail page when the course type is LearningPath.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '../ui';
import { CheckCircleIcon, PlayIcon, SchoolIcon } from '../icons';
import { EnrollmentStatus, CourseType } from '../../apis/api-client';
import { CourseProgressBar, SectionHeader, EmptyState } from '../common';

// Type for LearningPathCourse - will be generated after api-gen
interface LearningPathCourse {
  id: number;
  learningPathId: number;
  courseId: number;
  sequenceOrder: number;
  createdAt?: string;
  courseTitle?: string;
  courseThumbnail?: string;
  courseDescription?: string;
  courseSlug?: string;
  courseType?: CourseType;
  userCourseEnrollment?: {
    enrollmentId?: number;
    enrollmentStatus?: EnrollmentStatus;
    progressPercentage?: number;
    completedAt?: string;
  };
}

interface PathCourseListProps {
  pathCourses: LearningPathCourse[];
  completedPathCourses?: number;
  totalPathCourses?: number;
  enrollmentStatus?: EnrollmentStatus;
  progressPercentage?: number;
  locked?: boolean;
  learningPathSlug?: string;
}

function PathCourseItem({
  pathCourse,
  index,
  onClick,
}: {
  pathCourse: LearningPathCourse;
  index: number;
  onClick: () => void;
}) {
  const enrollment = pathCourse.userCourseEnrollment;
  const isCompleted = enrollment?.enrollmentStatus === EnrollmentStatus.Completed;
  const isInProgress = enrollment?.enrollmentStatus === EnrollmentStatus.InProgress;
  const isNotStarted = !enrollment || enrollment.enrollmentStatus === EnrollmentStatus.NotStarted;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: 'primary.main',
          backgroundColor: 'action.hover',
          boxShadow: 1,
        },
      }}
      onClick={onClick}
    >
      {/* Sequence Number / Status Icon */}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          backgroundColor: isCompleted ? 'success.light' : isInProgress ? 'primary.light' : 'grey.200',
          color: isCompleted ? 'success.dark' : isInProgress ? 'primary.dark' : 'grey.600',
        }}
      >
        {isCompleted ? (
          <CheckCircleIcon sx={{ fontSize: 24 }} />
        ) : isInProgress ? (
          <PlayIcon sx={{ fontSize: 20 }} />
        ) : (
          <Typography variant="body1" fontWeight="bold">
            {index + 1}
          </Typography>
        )}
      </Box>

      {/* Course Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {pathCourse.courseTitle}
        </Typography>

        {/* Progress for enrolled courses (skip Not Started — bar at 0% adds noise) */}
        {enrollment?.enrollmentStatus !== undefined &&
          enrollment.enrollmentStatus !== EnrollmentStatus.NotStarted && (
            <Box sx={{ mt: 1 }}>
              <CourseProgressBar
                value={enrollment.progressPercentage || 0}
                label={enrollment.enrollmentStatus === EnrollmentStatus.Completed ? 'Completed' : undefined}
                variant={enrollment.enrollmentStatus === EnrollmentStatus.Completed ? 'success' : 'primary'}
                height={18}
              />
            </Box>
        )}
      </Box>
    </Box>
  );
}

export default function PathCourseList({
  pathCourses,
  completedPathCourses = 0,
  totalPathCourses = 0,
  enrollmentStatus,
  progressPercentage,
  locked = false,
  learningPathSlug,
}: PathCourseListProps) {
  const navigate = useNavigate();
  const showProgress = !locked && enrollmentStatus !== undefined;

  // Sort courses by sequence order
  const sortedCourses = [...pathCourses].sort((a, b) => a.sequenceOrder - b.sequenceOrder);

  const handleCourseClick = (pathCourse: LearningPathCourse) => {
    // Navigate to the course detail page using slug, pass learning path slug in state
    const courseSlug = pathCourse.courseSlug || pathCourse.courseId.toString();
    navigate(`/courses/${courseSlug}`, { state: { fromLearningPath: learningPathSlug } });
  };

  if (sortedCourses.length === 0) {
    return (
      <EmptyState
        icon={<SchoolIcon sx={{ fontSize: 100, color: 'grey.400' }} />}
        title="No courses in this learning path"
        subtitle="Courses will appear here once they are added to the learning path"
      />
    );
  }

  return (
    <Box
      sx={{
        borderRadius: '12px',
        border: 'solid 1px rgb(228, 228, 228)',
        height: '100%',
        flex: 1,
        maxWidth: 500,
        padding: 2,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5" fontWeight="bold" textAlign="left">
          Learning Path Courses
        </Typography>
      </Box>

      {/* Progress Section */}
      {showProgress && (
        <Box sx={{ mt: 2, mb: 3, overflow: 'hidden' }}>
          <CourseProgressBar
            value={progressPercentage || 0}
            label={enrollmentStatus === EnrollmentStatus.Completed ? 'Completed' : undefined}
            variant={enrollmentStatus === EnrollmentStatus.Completed ? 'success' : 'primary'}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {completedPathCourses} of {totalPathCourses} courses completed
          </Typography>
        </Box>
      )}

      {/* Course List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {sortedCourses.map((pathCourse, index) => (
          <PathCourseItem
            key={pathCourse.id || pathCourse.courseId}
            pathCourse={pathCourse}
            index={index}
            onClick={() => handleCourseClick(pathCourse)}
          />
        ))}
      </Box>
    </Box>
  );
}
